import { ChangeDetectorRef, Directive, ElementRef, Input, OnChanges, OnDestroy, Optional, SimpleChange } from "@angular/core";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { OkrWorkflowAvatarGroupComponent } from "@webapp/okrs/components/okr-workflow/okr-workflow-avatar-group/okr-workflow-avatar-group.component";
import { UiAssigneeComponent } from "@webapp/ui/assignee/assignee.component";
import { UiAssigneeAvatarComponent } from "@webapp/ui/assignee/components/assignee-avatar/assignee-avatar.component";
import { UiAssigneeNameComponent } from "@webapp/ui/assignee/components/assignee-name/assignee-name.component";
import { AssigneeComponentInstances } from "../component-instance-tracker";
import { assigneeFromRedux } from "../utils/assignee.utils";

/**
 * This directive is used with ui-assignee and ui-assignee-avatar components from the UI Kit.
 * It transforms assignee ID to an assignee and binds it to the underlying component. Since
 * assignees are changed via Web Sockets, we will automatically refresh the bindings when
 * the corresponding socket message is received.
 */
@Directive({
  selector: "ui-assignee-avatar[fromAssigneeId], ui-assignee[fromAssigneeId], ui-assignee-name[fromAssigneeId]",
})
export class FromAssigneeIdDirective implements OnDestroy, OnChanges {
  @Input("fromAssigneeId")
  public assigneeId: string | string[];

  @Input()
  public hideDeleted: boolean;

  constructor(
    @Optional() private assigneeAvatarComponent: UiAssigneeAvatarComponent,
    @Optional() private assigneeComponent: UiAssigneeComponent,
    @Optional() private assigneeNameComponent: UiAssigneeNameComponent,
    private cd: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  private get component(): UiAssigneeAvatarComponent | UiAssigneeComponent | UiAssigneeNameComponent {
    return this.assigneeAvatarComponent || this.assigneeComponent || this.assigneeNameComponent;
  }

  public ngOnChanges(): void {
    AssigneeComponentInstances.set(this.elementRef.nativeElement, {
      assigneeIds: typeof this.assigneeId === "string" ? [this.assigneeId] : this.assigneeId ? this.assigneeId.filter((assignee) => assignee !== undefined) : [],
      refresh: this.markForCheck.bind(this),
    });

    this.setAssigneeToUnderlyingComponent();
  }

  public ngOnDestroy(): void {
    AssigneeComponentInstances.delete(this.elementRef.nativeElement);
  }

  private setAssigneeToUnderlyingComponent(): void {
    if (!reduxStoreContainer.reduxStore) {
      return;
    }

    const state: IAssigneesStoreState = reduxStoreContainer.reduxStore.getState();

    if (typeof this.assigneeId === "string") {
      this.component.uiAssignee = assigneeFromRedux(state, this.assigneeId);
    } else {
      // If it's an array, find the first valid assignee
      this.component.uiAssignee = (this.assigneeId || [])
        .map((assigneeId) => assigneeFromRedux(state, assigneeId, { hideDeleted: this.hideDeleted }))
        .filter((assignee) => assignee !== undefined)[0];
    }

    if ("ngOnChanges" in this.component) {
      this.component.ngOnChanges();
    }
  }

  private markForCheck(): void {
    this.setAssigneeToUnderlyingComponent();
    this.cd.markForCheck();
  }
}

/**
 * This directive is used with ui-assignee-avatar-group component from the UI Kit.
 * It transforms assignee ID to an assignee and binds it to the underlying component. Since
 * assignees are changed via Web Sockets, we will automatically refresh the bindings when
 * the corresponding socket message is received.
 */
@Directive({
  selector: "okr-workflow-avatar-group[fromAssigneeIds]",
})
export class FromAssigneeIdsDirective implements OnDestroy, OnChanges {
  @Input("fromAssigneeIds")
  public assigneeIds: string[];

  @Input()
  public hideDeleted = false;

  @Input()
  public startIndex = 0;

  constructor(private okrWorkflowAvatarGroup: OkrWorkflowAvatarGroupComponent, private cd: ChangeDetectorRef, private elementRef: ElementRef) {}

  public ngOnChanges(): void {
    AssigneeComponentInstances.set(this.elementRef.nativeElement, {
      assigneeIds: this.assigneeIds || [],
      refresh: this.markForCheck.bind(this),
    });

    this.setAssigneesToUnderlyingComponent();
  }

  public ngOnDestroy(): void {
    AssigneeComponentInstances.delete(this.elementRef.nativeElement);
  }

  private setAssigneesToUnderlyingComponent(): void {
    if (!reduxStoreContainer.reduxStore) {
      return;
    }

    const state: IAssigneesStoreState = reduxStoreContainer.reduxStore.getState();
    const oldAssignees = this.okrWorkflowAvatarGroup.assignees;

    let newAssignees = (this.assigneeIds || []).map((assigneeId) => assigneeFromRedux(state, assigneeId, { hideDeleted: this.hideDeleted }));

    if (this.hideDeleted) {
      newAssignees = newAssignees.filter((assignee) => assignee !== undefined);
    }

    if (this.startIndex > 0) {
      newAssignees = newAssignees.slice(this.startIndex);
    }

    this.okrWorkflowAvatarGroup.assignees = newAssignees;
    this.okrWorkflowAvatarGroup.ngOnChanges({ assignees: new SimpleChange(oldAssignees, newAssignees, false) });
  }

  private markForCheck(): void {
    this.setAssigneesToUnderlyingComponent();
    this.cd.markForCheck();
  }
}
